import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Divider, FormControlLabel, IconButton, List,
    ListItemButton, Paper, Stack, Switch, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import StopIcon from '@mui/icons-material/Stop';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SopChart from './sopChart';
import { useSopData } from './useSopData';
import { useSopNarration } from './useSopNarration';

const PP = "'Poppins', sans-serif";
const NX = '#e2733b';   // NexWave
const AS = '#2f8fd8';   // Astern

const cardSx = { border: '1px solid #E2E8F0', borderRadius: '12px', bgcolor: '#fff' };

// The document's four, in its order. 'all' is the default and the fallback whenever the
// data carries no roles at all.
const ROLE_KEYS = ['all', 'sales', 'admin', 'astern'];

/**
 * Steps that describe something done IN this app, and where to do it.
 *
 * Reading "upload the payment slip to NexWave" and then hunting for the screen is the
 * gap between a document and a tool. Keyed by step number, deliberately short: only
 * steps whose action has one obvious home. (user 2026-10-04)
 *
 * This lives here, not in the SOP data — the document is written for people who may not
 * have the CRM open, and the generated file must stay a faithful copy of it.
 */
// Where a step is actually carried out. A step may send you to more than one place —
// step 9 both chases a payment on the Scheduling board and uploads the slip on the sale —
// so each entry is a list.
const STEP_ACTIONS = {
    1: [{ label: 'Create a sale', to: '/sales/create' }],
    // 5 and 6 SWAPPED in v58: scheduling now comes first, and pricing + the 60% payment
    // follows it — "schedule is out, then need check pricing and get payment. so
    // scheduling first right?" (user, SOP meeting 2026-10-05). So the board button moved
    // from 6 to 5, and the slip upload from 5 to 6. Keyed by step number, which is exactly
    // why a renumbering has to be followed through here.
    5: [{ label: 'Open the Scheduling board', to: '/sales/scheduling' }],
    6: [{ label: 'Upload the slip on the sale', to: '/sales' }],
    // Step 8 ends with the card slip being uploaded to NexWave (8.7 in v50), so it earns
    // the same button as 5 and 9 — the step says upload it, the button goes there.
    8: [{ label: 'Upload the slip on the sale', to: '/sales' }],
    // 9.3 sends sales to the Scheduling board to chase what the Remarks column flags as
    // Pending payment; 9.5 uploads the slip once it is paid. (SOP v51)
    9: [
        { label: 'Open the Scheduling board', to: '/sales/scheduling' },
        { label: 'Upload the slip on the sale', to: '/sales' },
    ],
};

/* ============================================================================
   THE OPERATIONS SOP — drawn by the app

   The whole order-to-after-sales process, one flowchart per step, with a spoken
   walkthrough in English, Chinese and Malay.

   The reference document is exactly that: reference. Everything here is ours, so a
   tap on a box is an ordinary React event, "→ Step 3" routes through the app's own
   router, the walkthrough can actually be paused, and changing speed cannot make it
   run ahead of the voice. (user 2026-10-03: "please dont embed only html. make it
   into the app. html just for your reference")

   The content is fetched, never bundled — see useSopData for why.

   Deep links: /resources/sop · /resources/sop/step-5 · /resources/sop/atap ·
   /resources/sop/status · /resources/sop/fix
   ========================================================================= */
export default function SopPage() {
    const { data, error, loading } = useSopData();

    if (error) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography sx={{ fontFamily: PP, color: '#64748B', fontSize: '0.9rem' }}>{error}</Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
                <CircularProgress size={26} />
                <Typography sx={{ fontFamily: PP, color: '#64748B', fontSize: '0.88rem' }}>
                    Loading the SOP…
                </Typography>
            </Stack>
        );
    }

    return <SopContent data={data} />;
}

function SopContent({ data }) {
    const navigate = useNavigate();
    const { page } = useParams();                       // undefined = overview
    const key = page || 'overview';

    // Held by identity, not rebuilt each render: the walkthrough's lines and the
    // wording lookup are memoised on these, and a fresh `{}` every render would
    // rebuild both on every keystroke of state.
    const { SOP, FLOWS, TOURS, UI, ROLES } = useMemo(() => ({
        ROLES: data.roles || {},
        SOP: data.sop || {},
        FLOWS: data.flows || {},
        TOURS: data.tours || {},
        UI: data.ui || {},
    }), [data]);
    const version = data.version || '';

    const [lang, setLang] = useState(() => {
        try { return localStorage.getItem('sop-lang') || 'en'; } catch { return 'en'; }
    });
    // Whose work to show. The same four choices and the same memory key the document
    // uses, so a person who has picked a role in one sees it in the other.
    // (user 2026-10-04)
    const hasRoles = Object.keys(ROLES).length > 0;
    const [role, setRole] = useState(() => {
        try {
            const r = localStorage.getItem('sop-role') || 'all';
            return ROLE_KEYS.includes(r) ? r : 'all';
        } catch { return 'all'; }
    });
    const activeRole = hasRoles ? role : 'all';
    const [selectedId, setSelectedId] = useState(null);

    const t = useCallback((k) => (UI[lang] || UI.en || {})[k] || (UI.en || {})[k] || k, [UI, lang]);

    // A box that only leads somewhere - "→ Step 7", "→ ATAP checklist" - belongs to
    // nobody and is always shown; the document draws the same line.
    const isJump = (id) => /^(G\d|PG)/.test(id || '');
    const mine = useCallback((chartKey, id) =>
        activeRole === 'all' || isJump(id) ||
        ((ROLES[chartKey] || {})[id] || []).includes(activeRole), [ROLES, activeRole]);
    const stepHasRole = useCallback((st) =>
        activeRole === 'all' ||
        Object.values(ROLES[String(st.n)] || {}).some(v => v.includes(activeRole)),
        [ROLES, activeRole]);
    const pick = useCallback((o) => (o ? (o[lang] ?? o.en) : ''), [lang]);

    const allSteps = SOP.steps || [];
    // A step with nothing for this role is not in their copy of the SOP at all - it is
    // not merely faded, as a box is.
    const steps = allSteps.filter(stepHasRole);
    const pages = SOP.pages || [];
    const stepNo = key.startsWith('step-') ? key.slice(5) : null;
    const infoPage = pages.find(p => p.key === key) || null;
    const isFix = key === 'fix';

    const flowKey = stepNo || (key === 'overview' ? 'overview' : null);
    const chartSource = flowKey ? (FLOWS[lang] || FLOWS.en || {})[flowKey] : null;

    // The walkthrough: the node ids live on the English script, the words come from
    // the chosen language, line for line.
    const lines = useMemo(() => {
        if (!flowKey) return [];
        const en = (TOURS.en || {})[flowKey] || [];
        const mineLang = (TOURS[lang] || {})[flowKey] || [];
        // English carries [id, text] pairs; zh and ms carry the translated line as a
        // plain STRING, in the same order. Taking [1] of a string gives its second
        // CHARACTER - which is why a Chinese caption read "件" and the voice had one
        // character to say. (user 2026-10-04: "chinese and malay doesnt really read all")
        const textOf = (row, fallback) =>
            typeof row === 'string' ? row
                : Array.isArray(row) ? row[1]
                    : fallback;

        return en
            .map((row, i) => [row[0], textOf(mineLang[i], row[1])])
            .filter(([id]) => mine(flowKey, id));
    }, [TOURS, flowKey, lang, mine]);

    const narration = useSopNarration({ lines, lang });
    const { index, playing, rate, voiceOn, noVoice, total, reset } = narration;
    const currentNode = index >= 0 ? lines[index]?.[0] : null;
    const currentText = index >= 0 ? lines[index]?.[1] : '';

    // Changing page or language starts the walkthrough over rather than leaving it
    // pointing at a box that is no longer on screen.
    useEffect(() => { reset(); setSelectedId(null); }, [key, lang, activeRole, reset]);

    const setRolePref = (r) => {
        if (!r) return;
        setRole(r);
        try { localStorage.setItem('sop-role', r); } catch { /* private mode */ }
    };

    const setLangPref = (l) => {
        if (!l) return;
        setLang(l);
        try { localStorage.setItem('sop-lang', l); } catch { /* private mode */ }
    };

    const isOffForRole = useCallback((id) => !mine(flowKey, id), [mine, flowKey]);

    /** A tap on a box: a "→ Step n" or "→ page" box navigates, anything else highlights. */
    const onNodeClick = useCallback((id) => {
        const step = id.match(/^G(\d+)/);                    // G3, G8b → step 3, 8
        if (step) { navigate(`/resources/sop/step-${step[1]}`); return; }
        const pg = id.match(/^PG([A-Za-z]+)/);               // PGatap → the ATAP page
        if (pg) { navigate(`/resources/sop/${pg[1].toLowerCase()}`); return; }
        if (key === 'overview') {
            const s = id.match(/^S(\d+)$/);                  // S5 on the overview → step 5
            if (s) { navigate(`/resources/sop/step-${s[1]}`); return; }
        }
        setSelectedId(prev => (prev === id ? null : id));
    }, [navigate, key]);

    const step = stepNo ? allSteps.find(s => String(s.n) === stepNo) : null;
    const idx = step ? steps.indexOf(step) : -1;
    const prevStep = idx > 0 ? steps[idx - 1] : null;
    const nextStep = idx >= 0 && idx < steps.length - 1 ? steps[idx + 1] : null;

    const title = step ? `${step.n} ${pick(step.t)}`
        : infoPage ? pick(infoPage.title)
            : isFix ? t('fix')
                : t('overview');

    const NavItem = ({ to, dot, label, right, on }) => (
        <ListItemButton
            selected={on}
            onClick={() => navigate(to)}
            sx={{
                borderRadius: '8px', py: 0.75, px: 1.25, mb: 0.25,
                '&.Mui-selected': { bgcolor: '#FEF3C7', '&:hover': { bgcolor: '#FDE68A' } },
            }}
        >
            {dot && (
                <Box sx={{ width: 11, height: 11, borderRadius: '3px', background: dot, mr: 1, flex: 'none' }} />
            )}
            <Typography sx={{ fontFamily: PP, fontSize: '0.84rem', fontWeight: on ? 700 : 400, color: '#1d2330', flex: 1 }}>
                {label}
            </Typography>
            {right && (
                <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: '#64748B', ml: 1, whiteSpace: 'nowrap' }}>
                    {right}
                </Typography>
            )}
        </ListItemButton>
    );

    return (
        <Box sx={{ width: '100%', fontFamily: PP, pb: 6 }}>
            {/* header */}
            <Stack direction="row" alignItems="flex-start" sx={{ mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, lineHeight: 1.2 }}>
                            SOP
                        </Typography>
                        {version && (
                            <Chip size="small" label={(version.match(/v\d+/i) || [version])[0]}
                                sx={{ fontFamily: PP, fontSize: '0.68rem', bgcolor: '#F1F5F9', color: '#64748B' }} />
                        )}
                    </Stack>
                    <Stack direction="row" spacing={1.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Box sx={{ width: 11, height: 11, borderRadius: '3px', bgcolor: NX }} />
                            <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: '#64748B' }}>
                                {t('legNx')}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Box sx={{ width: 11, height: 11, borderRadius: '3px', bgcolor: AS }} />
                            <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: '#64748B' }}>
                                {t('legAs')}
                            </Typography>
                        </Stack>
                        <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: '#94A3B8' }}>
                            {t('legQ')}
                        </Typography>
                    </Stack>
                </Box>
                <Box sx={{ flex: 1 }} />
                <ToggleButtonGroup size="small" exclusive value={lang} onChange={(_, v) => setLangPref(v)}>
                    <ToggleButton value="en" sx={{ fontFamily: PP, textTransform: 'none', px: 1.5 }}>EN</ToggleButton>
                    <ToggleButton value="zh" sx={{ fontFamily: PP, textTransform: 'none', px: 1.5 }}>中文</ToggleButton>
                    <ToggleButton value="ms" sx={{ fontFamily: PP, textTransform: 'none', px: 1.5 }}>BM</ToggleButton>
                </ToggleButtonGroup>
                {/* Whose work to show. Only offered when the data says which boxes belong
                    to whom - an older SOP has no roles and gets no switch. */}
                {hasRoles && (
                    <ToggleButtonGroup size="small" exclusive value={activeRole}
                        onChange={(_, v) => setRolePref(v)}>
                        {ROLE_KEYS.map(r => (
                            <ToggleButton key={r} value={r} sx={{ fontFamily: PP, textTransform: 'none', px: 1.5 }}>
                                {t('role' + r.charAt(0).toUpperCase() + r.slice(1))}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                )}
                {/* A gap in the SOP arrives attached to the step it was read in, rather than
                    as a sentence somebody has to decode. */}
                <Button variant="outlined" size="small" startIcon={<SupportAgentIcon />}
                    onClick={() => navigate(`/support/new?related=Sop&ref=${encodeURIComponent(key)}`)}
                    sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px' }}>
                    Something unclear?
                </Button>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
                {/* ── the step list ─────────────────────────────────────── */}
                <Paper elevation={0} sx={{ ...cardSx, width: { xs: '100%', md: 280 }, flexShrink: 0, p: 1 }}>
                    <List dense disablePadding>
                        <NavItem to="/resources/sop" label={t('overview')} on={key === 'overview'}
                            dot={`linear-gradient(135deg,${NX} 50%,${AS} 50%)`} />
                        <Divider sx={{ my: 0.75 }} />
                        {steps.map(s => (
                            <NavItem key={s.n} to={`/resources/sop/step-${s.n}`}
                                dot={s.co === 'nx' ? NX : AS}
                                label={`${s.n} ${pick(s.t)}`} right={pick(s.who)}
                                on={stepNo === String(s.n)} />
                        ))}
                        <Divider sx={{ my: 0.75 }} />
                        {pages.map(p => (
                            <NavItem key={p.key} to={`/resources/sop/${p.key}`}
                                dot="#94A3B8" label={pick(p.title)} on={key === p.key} />
                        ))}
                        <NavItem to="/resources/sop/fix" dot="#94A3B8" label={t('fix')} on={isFix} />
                    </List>
                </Paper>

                {/* ── the page ──────────────────────────────────────────── */}
                <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1.3rem', color: '#0F172A' }}>
                        {title}
                    </Typography>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#64748B', mb: activeRole === 'all' ? 1.5 : 0.75 }}>
                        {step ? `${t('inCharge')}: ${pick(step.who)}`
                            : infoPage ? pick(infoPage.sub)
                                : isFix ? t('fixSub') : t('whole')}
                    </Typography>
                    {/* Where this step is actually done, when it is done in here. */}
                    {step && (STEP_ACTIONS[step.n] || []).map(a => (
                        <Button key={a.to} size="small" variant="outlined"
                            endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
                            onClick={() => navigate(a.to)}
                            sx={{
                                fontFamily: PP, textTransform: 'none', borderRadius: '8px',
                                fontSize: '0.76rem', mb: 1.5, mr: 1,
                            }}>
                            {a.label}
                        </Button>
                    ))}

                    {/* Said plainly, because a filtered chart with no explanation looks
                        like a chart with pieces missing. */}
                    {activeRole !== 'all' && (
                        <Typography sx={{
                            fontFamily: PP, fontSize: '0.78rem', color: '#92400E',
                            bgcolor: '#FFFBEB', border: '1px solid #FDE68A',
                            borderRadius: '8px', px: 1.25, py: 0.75, mb: 1.5, display: 'inline-block',
                        }}>
                            {String(t('roleNote')).replace('{role}',
                                t('role' + activeRole.charAt(0).toUpperCase() + activeRole.slice(1)))}
                        </Typography>
                    )}

                    {/* ── Play ──────────────────────────────────────────── */}
                    {lines.length > 0 && (
                        <Paper elevation={0} sx={{ ...cardSx, p: 1.25, mb: 1.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                                <Button variant="contained" onClick={narration.toggle}
                                    startIcon={playing ? <PauseIcon /> : <PlayArrowIcon />}
                                    sx={{
                                        // White on the filled button. Dark text on a strong fill
                                        // read as a disabled control. (user 2026-10-04: "the
                                        // button shd be white word right? bit weird")
                                        fontFamily: PP, textTransform: 'none', fontWeight: 700, borderRadius: '8px',
                                        bgcolor: '#F5A623', color: '#fff', '&:hover': { bgcolor: '#E0940F' },
                                        '& .MuiButton-startIcon': { color: '#fff' },
                                    }}>
                                    {playing ? t('pause') : index >= 0 ? t('resume') : t('play')}
                                </Button>
                                <IconButton size="small" onClick={narration.prev} disabled={index <= 0}
                                    title={t('prev')} sx={{ border: '1px solid #E2E8F0', borderRadius: '8px' }}>
                                    <SkipPreviousIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                                <IconButton size="small" onClick={narration.next} disabled={index >= total - 1}
                                    title={t('next')} sx={{ border: '1px solid #E2E8F0', borderRadius: '8px' }}>
                                    <SkipNextIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                                {index >= 0 && (
                                    <IconButton size="small" onClick={narration.reset}
                                        sx={{ border: '1px solid #E2E8F0', borderRadius: '8px' }}>
                                        <StopIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                )}

                                <ToggleButtonGroup size="small" exclusive value={rate}
                                    onChange={(_, v) => v && narration.setRate(v)}>
                                    {[1, 1.25, 1.5].map(r => (
                                        <ToggleButton key={r} value={r} sx={{ fontFamily: PP, textTransform: 'none', px: 1.25 }}>
                                            {r}x
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>

                                <FormControlLabel
                                    control={<Switch size="small" checked={voiceOn}
                                        onChange={e => narration.setVoiceOn(e.target.checked)} />}
                                    label={<Typography sx={{ fontFamily: PP, fontSize: '0.8rem' }}>{t('voice')}</Typography>}
                                />
                                <Box sx={{ flex: 1 }} />
                                {index >= 0 && (
                                    <Chip size="small" label={`${index + 1} / ${total}`}
                                        sx={{ fontFamily: PP, fontWeight: 700, bgcolor: '#F1F5F9', color: '#475569' }} />
                                )}
                            </Stack>

                            {index >= 0 && (
                                <Box sx={{
                                    mt: 1.25, p: 1.5, borderRadius: '10px',
                                    bgcolor: '#FFFBEB', border: '2px solid #F5A623',
                                }}>
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.95rem', color: '#1d2330', lineHeight: 1.45 }}>
                                        {currentText}
                                    </Typography>
                                    {!playing && index === total - 1 && (
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: '#92400E', mt: 0.5 }}>
                                            {t('done')}
                                        </Typography>
                                    )}
                                    {noVoice && (
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#92400E', mt: 0.5 }}>
                                            {t('novoice')}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Paper>
                    )}

                    {/* ── the chart, or a reference page ────────────────── */}
                    {chartSource && (
                        <Paper elevation={0} sx={{ ...cardSx, p: 1.5 }}>
                            <SopChart
                                source={chartSource}
                                highlightId={currentNode}
                                selectedId={selectedId}
                                onNodeClick={onNodeClick}
                                isOff={isOffForRole}
                            />
                            <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: '#64748B', mt: 1 }}>
                                {t('tip')}
                            </Typography>
                        </Paper>
                    )}

                    {/* One card for the whole checklist, its sections set off by a heading
                        and a line of space rather than by a card each - a page of cards read
                        as separate documents when it is one list.
                        (user 2026-10-04: "combine, the checklist ... info checklist tgt as
                        well in one card") */}
                    {infoPage && (
                        <Paper elevation={0} sx={{ ...cardSx, p: 2.5 }}>
                            {(infoPage.sections || []).map((sec, i) => (
                                <Box key={i} sx={{ mt: i === 0 ? 0 : 2.5 }}>
                                    {pick(sec.h) && (
                                        <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '0.92rem', color: '#0F172A', mb: 1 }}>
                                            {pick(sec.h)}
                                        </Typography>
                                    )}
                                    <Stack spacing={0.5}>
                                        {(sec.items || []).map((it, j) => (
                                            <Stack key={j} direction="row" spacing={1} alignItems="center">
                                                <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#CBD5E1', flex: 'none' }} />
                                                <Typography sx={{ fontFamily: PP, fontSize: '0.86rem', color: '#1E293B' }}>
                                                    {pick(it.t)}
                                                </Typography>
                                                {/* A line that names the step it comes from is a way in to that step. */}
                                                {it.step != null && (
                                                    <Chip size="small" clickable
                                                        label={String(t('stepN')).replace('{n}', it.step)}
                                                        onClick={() => navigate(`/resources/sop/step-${it.step}`)}
                                                        sx={{
                                                            fontFamily: PP, fontSize: '0.7rem', height: 22,
                                                            bgcolor: '#F1F5F9', color: '#475569',
                                                            '&:hover': { bgcolor: '#FEF3C7' },
                                                        }} />
                                                )}
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Box>
                            ))}
                        </Paper>
                    )}

                    {isFix && (
                        <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
                            <Stack spacing={1}>
                                {((SOP.fix || {})[lang] || (SOP.fix || {}).en || []).map((f, i) => (
                                    <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#F59E0B', mt: '7px', flex: 'none' }} />
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.88rem', color: '#1E293B' }}>{f}</Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Paper>
                    )}

                    {/* ── previous / next ───────────────────────────────── */}
                    {step && (
                        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
                            <Button variant="outlined" fullWidth
                                onClick={() => navigate(prevStep ? `/resources/sop/step-${prevStep.n}` : '/resources/sop')}
                                sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '10px', justifyContent: 'flex-start', py: 1.25 }}>
                                <Box sx={{ textAlign: 'left' }}>
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#64748B' }}>
                                        {prevStep ? t('prevStep') : t('backTo')}
                                    </Typography>
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.86rem', color: '#1d2330' }}>
                                        {prevStep ? `${prevStep.n} ${pick(prevStep.t)}` : t('overview')}
                                    </Typography>
                                </Box>
                            </Button>
                            <Button variant="outlined" fullWidth
                                onClick={() => navigate(nextStep ? `/resources/sop/step-${nextStep.n}` : '/resources/sop')}
                                sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '10px', justifyContent: 'flex-end', py: 1.25 }}>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#64748B' }}>
                                        {nextStep ? t('nextStep') : t('backTo')}
                                    </Typography>
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.86rem', color: '#1d2330' }}>
                                        {nextStep ? `${nextStep.n} ${pick(nextStep.t)}` : t('overview')}
                                    </Typography>
                                </Box>
                            </Button>
                        </Stack>
                    )}
                </Box>
            </Stack>
        </Box>
    );
}
